import React from 'react'

import { Button, Group, Space, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { observer } from 'mobx-react-lite'

import { useUpgrade } from '#components/Modals'
import { STRIPE_TIER } from '#components/Settings/Org/plans'
import { useCanUpgrade } from '#components/Settings/Org/useBillingPlan'
import { IOrg, useMst } from '#state'
import { isEmailValid } from '#util'

import { OnboardingPage } from '../OnboardingPage'

import { TemplateCard } from './TemplateCard'
import {
  NewWorkspaceFormValues,
  TemplateWorkspaceListing,
  useActiveTemplates,
  useCreateWorkspaceMutation,
} from './util'

type ServerErrorCode = 'unknown'

export type NewWorkspaceProps = {
  onSubmit: (values: NewWorkspaceFormValues) => void
  userEmail: string
  serverError?: ServerErrorCode
  loading?: boolean
  phase: 'pick-template' | 'create-workspace'
  switchPhase: () => void
  templates: TemplateWorkspaceListing[]
  homeButtonLabel?: string
}

const serverErrorMessages: Record<ServerErrorCode, string | React.ReactNode> = {
  unknown: 'An unknown error occurred',
}

const optionalEmail = (v: string) =>
  v === '' || isEmailValid(v) ? null : 'Not a valid email'

export const NewWorkspace = ({
  onSubmit,
  userEmail,
  loading,
  serverError,
  phase,
  switchPhase,
  templates,
  homeButtonLabel,
}: NewWorkspaceProps) => {
  const form = useForm<NewWorkspaceFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      templateCode: '',
      templateName: '',
      name: '',
      email: '',
      email2: '',
    },
    validate: {
      name: (value) => {
        if (phase === 'create-workspace' && value.length === 0) {
          return 'Value is required'
        }
      },
      email: optionalEmail,
      email2: optionalEmail,
    },
  })

  const errorMessage = serverError
    ? serverErrorMessages[serverError]
    : undefined

  // New workspace copies the tier of its template. Legacy tiers offered
  // three collaborators. New Stripe plans offer collaboration as a paid feature
  const selectedTemplate = templates.find(
    (t) => t.code === form.values.templateCode,
  )
  const inviteFieldsVisible = selectedTemplate?.tier !== STRIPE_TIER

  const title =
    phase === 'pick-template'
      ? 'What kind of project are you working on?'
      : 'Create your workspace'

  return (
    <OnboardingPage title={title} loading={loading}>
      <OnboardingPage.Buttons
        includedButtons={['logout']}
        homeButtonLabel={homeButtonLabel}
      />
      <OnboardingPage.Form
        onSubmit={form.onSubmit(onSubmit)}
        errorMessage={errorMessage}
      >
        {phase === 'pick-template' && (
          <Stack gap={0}>
            {templates.map((t) => (
              <TemplateCard
                key={t.code}
                template={t}
                selected={form.values.templateCode === t.code}
                onSelect={() => {
                  form.setFieldValue('templateCode', t.code)
                  form.setFieldValue('templateName', t.name)
                }}
              />
            ))}
          </Stack>
        )}
        {phase === 'create-workspace' && (
          <>
            <TextInput
              size="md"
              label="Workspace name"
              {...form.getInputProps('name')}
            />
            {inviteFieldsVisible && (
              <>
                <Text fw="bold">
                  Invite collaborators by email <br />
                  <Text size="sm" span fw={400}>
                    (You can also do this later)
                  </Text>
                </Text>
                <TextInput
                  type="email"
                  autoComplete="off"
                  placeholder="laverne@shotz.com"
                  {...form.getInputProps('email')}
                />

                <TextInput
                  type="email"
                  autoComplete="off"
                  placeholder="shirley@shotz.com"
                  {...form.getInputProps('email2')}
                />
              </>
            )}
          </>
        )}
        <Group justify="center">
          <Button
            onClick={switchPhase}
            variant={phase === 'create-workspace' ? 'outline' : 'filled'}
            disabled={phase === 'pick-template' && !form.values.templateCode}
          >
            {phase === 'create-workspace' ? 'Back' : 'Next'}
          </Button>
          {phase === 'create-workspace' && (
            <Button type="submit">Create Workspace</Button>
          )}
        </Group>
      </OnboardingPage.Form>
      <Stack align="center">
        <Space h="sm" />
        {/* high contrast 'dimmed' for dark backgrounds */}
        <Text fw={400} c="#a9a9ad">
          You&apos;re logged in as&nbsp;
          <Text size="sm" span fw="bold">
            {userEmail}
          </Text>
        </Text>
      </Stack>
      <OnboardingPage.Links types={['support']} />
    </OnboardingPage>
  )
}

const SubscriptionRequired = observer(function SubscriptionRequired({
  org,
  homeButtonLabel,
}: {
  org: IOrg
  homeButtonLabel?: string
}) {
  const { pending, upgrade } = useUpgrade()

  return (
    <OnboardingPage title="Upgrade to a plan to create more workspaces">
      <OnboardingPage.Buttons
        includedButtons={['logout']}
        homeButtonLabel={homeButtonLabel}
      />
      <Stack align="center">
        <Text size="lg" fw="bold" c="white" ta="center">
          Your account doesn&rsquo;t have a paid plan yet.
        </Text>
        <Button size="md" loading={pending} onClick={() => upgrade(org)}>
          See plans
        </Button>
      </Stack>
      <OnboardingPage.Links types={['support']} />
    </OnboardingPage>
  )
})

export const NewWorkspaceRoute = observer(function NewWorkspaceRoute() {
  const { user, currentOrg } = useMst()
  const templatesQuery = useActiveTemplates()
  const createMutation = useCreateWorkspaceMutation()
  const canUpgrade = useCanUpgrade(currentOrg)
  const loading = templatesQuery.isLoading || createMutation.isPending
  const [phase, setPhase] =
    React.useState<NewWorkspaceProps['phase']>('pick-template')
  const togglePhase = () => {
    if (phase === 'pick-template') {
      setPhase('create-workspace')
    } else {
      setPhase('pick-template')
    }
  }

  const homeButtonLabel = currentOrg?.name
    ? `Go to "${currentOrg.name}"`
    : undefined

  if (currentOrg?.isFreeStripeAccount && canUpgrade) {
    return (
      <SubscriptionRequired
        org={currentOrg}
        homeButtonLabel={homeButtonLabel}
      />
    )
  }

  return (
    <NewWorkspace
      serverError={createMutation.isError ? 'unknown' : undefined}
      onSubmit={createMutation.mutate}
      templates={templatesQuery.list}
      loading={loading}
      phase={phase}
      switchPhase={togglePhase}
      userEmail={user.email}
      homeButtonLabel={homeButtonLabel}
    />
  )
})
